"use client";

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { retrieveUserKycDetails } from "../../redux/actions/users";
import CustomLoaderComponent from "../Common/Loader/Loader";
import { formatDate } from "../../utils";
import userService from "../../redux/services/user.service";
import { Slide, toast } from "react-toastify";

function KycVerificationReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { kycDetails, loadingKycDetails } = useSelector((state) => state.user);
  const [updatingKyc, setUpdatingKyc] = React.useState(false);
  const [rejectingKyc, setRejectingKyc] = React.useState(false);
  const [submission, setSubmission] = useState(null);
  const [activeDocument, setActiveDocument] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState(
    kycDetails?.verificationNotes
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [rejectionNotes, setRejectionNotes] = React.useState(null);

  const getKycCount = React.useCallback(async () => {
    const res = await userService.getUserRejectionCount(id);
    setRejectionNotes(res.data?.data);
  }, [id]);
  React.useEffect(() => {
    dispatch(retrieveUserKycDetails(id));
    getKycCount();
  }, [dispatch, id]);

  useEffect(() => {
    // In a real application, you would fetch the submission data from your API
    if (id && kycDetails?.publicId) {
      setSubmission(kycDetails.publicId);
    }
  }, [id, kycDetails]);

  if (!submission) {
    return <div className="loading">Loading verification data...</div>;
  }

  const handleDocumentClick = (docType) => {
    setActiveDocument(docType);
  };

  const closeDocumentViewer = () => {
    setActiveDocument(null);
  };

  const handleNotesChange = (e) => {
    setVerificationNotes(e.target.value);
  };

  const handleRejectionReasonChange = (e) => {
    setRejectionReason(e.target.value);
  };

  const handleApprove = async () => {
    // if (
    //   !kycDetails?.supportiveDoc ||
    //   !kycDetails?.incorporationCertificate ||
    //   !kycDetails?.supportiveDocFront ||
    //   !kycDetails?.supportiveDocBack
    // ) {
    //   return toast("user cannot be verified without docs", {
    //     transition: Slide,
    //     closeButton: true,
    //     autoClose: 3000,
    //     position: "top-right",
    //     type: "error",
    //   });
    // }
    setUpdatingKyc(true);
    try {
      const res = await userService.updateKYCStatus({
        verificationNotes,
        id,
        isGovIdVerified: true,
      });
      if (res.status === 200) {
        toast(res?.data?.message, {
          transition: Slide,
          closeButton: true,
          autoClose: 3000,
          position: "top-right",
          type: "success", // info/success/warning/error
        });
        setSubmission((prev) => ({
          ...prev,
          status: "approved",
        }));
        getKycCount();
        dispatch(retrieveUserKycDetails(id));
      }
    } catch (error) {
      toast(error?.response?.data.message, {
        transition: Slide,
        closeButton: true,
        autoClose: 3000,
        position: "top-right",
        type: "error",
      });
    } finally {
      setUpdatingKyc(false);
    }
  };

  const handleReject = () => {
    setShowRejectionForm(true);
  };

  const confirmRejection = async () => {
    if (!rejectionReason.trim()) {
      toast("Please provide a reason for rejection", {
        transition: Slide,
        closeButton: true,
        autoClose: 3000,
        position: "top-right",
        type: "error",
      });
      return;
    }
    // if (
    //   !kycDetails?.supportiveDoc ||
    //   !kycDetails?.incorporationCertificate ||
    //   !kycDetails?.supportiveDocFront ||
    //   !kycDetails?.supportiveDocBack
    // ) {
    //   return toast("user cannot be verified without docs", {
    //     transition: Slide,
    //     closeButton: true,
    //     autoClose: 3000,
    //     position: "top-right",
    //     type: "error",
    //   });
    // }

    setRejectingKyc(true);
    try {
      const res = await userService.updateKYCStatus({
        isGovIdVerified: false,
        id,
        verificationNotes: rejectionReason,
      });
      if (res.status === 200) {
        toast(res?.data?.message, {
          transition: Slide,
          closeButton: true,
          autoClose: 3000,
          position: "top-right",
          type: "success", // info/success/warning/error
        });
        setSubmission((prev) => ({
          ...prev,
          status: "rejected",
        }));
        setShowRejectionForm(false);
        setRejectionReason("");
        getKycCount();
        dispatch(retrieveUserKycDetails(id));
      }
    } catch (error) {
      toast(error?.response?.data.message, {
        transition: Slide,
        closeButton: true,
        autoClose: 3000,
        position: "top-right",
        type: "error",
      });
    } finally {
      setRejectingKyc(false);
    }
  };

  const cancelRejection = () => {
    setShowRejectionForm(false);
    setRejectionReason("");
  };

  const handleBackToList = () => {
    navigate("/user/kyc");
  };

  if (loadingKycDetails) {
    return <CustomLoaderComponent />;
  }

  let status = "pending";
  if (
    kycDetails?.govtIdVerifiedAt !== null &&
    kycDetails?.isGovIdVerified === 0
  ) {
    status = "rejected";
  } else if (
    kycDetails?.govtIdVerifiedAt !== null &&
    kycDetails?.isGovIdVerified === 1
  ) {
    status = "approved";
  }

  console.log(rejectionNotes);
  return (
    <div className="reviewPanel">
      <header className="panelHeader">
        <div className="headerActions">
          <button className="backButton" onClick={handleBackToList}>
            ← Back to List
          </button>
        </div>
        <h1>KYC Verification Review</h1>
        <div className="submissionMeta">
          <span className="submissionId">ID: {kycDetails?.publicId}</span>
          <span className="submissionDate">
            Submitted: {formatDate(kycDetails?.govtIdSubmittedAt)}
          </span>
          <span className={`submissionStatus ${submission.status}`}>
            Status: {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>
      </header>

      <div className="reviewContent">
        <section className="infoSection">
          {/* {
        publicId: 'IfcK1776',
        firstName: 'Ammad',
        lastName: 'dada',
        email: 'azss@yopmail.com',
        govtIdSubmittedAt: null,
        accountType: 'personal',
        isGovIdVerified: 0,
        dateOfBirth: '2021-03-03T00:00:00.000Z',
        companyName: null,
        positionInCompany: null,
        companyNumber: null,
        companyAddress: null,
        vatNumber: null,
        verificationNotes: null,
        supportiveDoc: null,
        supportiveDocFront: null,
        supportiveDocBack: null,
        vatCertificate: null,
        incorporationCertificate: null,
        faceImage: null,
        profileImage: null
      } */}
          <h2>Personal Information</h2>
          <div className="infoGrid">
            <div className="infoItem">
              <label>Full Name</label>
              <div>
                {kycDetails?.firstName} {kycDetails?.lastName}
              </div>
            </div>
            <div className="infoItem">
              <label>Date of Birth</label>
              <div>{formatDate(kycDetails?.dateOfBirth)}</div>
            </div>
            <div className="infoItem">
              <label>username</label>
              <div>{kycDetails?.username}</div>
            </div>
            <div className="infoItem">
              <label>Address</label>
              <div>{kycDetails?.companyAddress}</div>
            </div>
            <div className="infoItem">
              <label>Rejection</label>
              <div>{rejectionNotes?.rejectionCount}</div>
            </div>
            <div className="infoItem">
              <label>Rejection Note</label>
              <div>
                {Array.isArray(rejectionNotes?.rejectionNotes)
                  ? rejectionNotes?.rejectionNotes.map((item) => (
                      <p>{item?.notes}</p>
                    ))
                  : "N/A"}
              </div>
            </div>
          </div>
        </section>

        {kycDetails?.accountType === "business" && (
          <section className="infoSection">
            <h2>Business Information</h2>
            <div className="infoGrid">
              <div className="infoItem">
                <label>Business Name</label>
                <div>{kycDetails?.companyName}</div>
              </div>
              <div className="infoItem">
                <label>Company Number</label>
                <div>{kycDetails?.companyNumber}</div>
              </div>
              <div className="infoItem">
                <label>VAT Number</label>
                <div>{kycDetails?.vatNumber ?? "N/A"}</div>
              </div>
            </div>
          </section>
        )}

        <section className="documentsSection">
          <h2>Submitted Documents</h2>
          <div className="documentsGrid">
            <div
              className="documentItem"
              onClick={() => handleDocumentClick("supportiveDocFront")}
            >
              <div className="documentThumbnail">
                <img
                  src={kycDetails?.supportiveDocFront || "/placeholder.svg"}
                  alt="ID Front"
                />
              </div>
              <div className="documentLabel">ID Front</div>
            </div>
            <div
              className="documentItem"
              onClick={() => handleDocumentClick("supportiveDocBack")}
            >
              <div className="documentThumbnail">
                <img
                  src={kycDetails?.supportiveDocBack || "/placeholder.svg"}
                  alt="ID Back"
                />
              </div>
              <div className="documentLabel">ID Back</div>
            </div>
            <div
              className="documentItem"
              onClick={() => handleDocumentClick("faceImage")}
            >
              <div className="documentThumbnail">
                <img
                  src={kycDetails?.faceImage || "/placeholder.svg"}
                  alt="Selfie"
                />
              </div>
              <div className="documentLabel">Selfie with ID</div>
            </div>
            {kycDetails?.accountType === "business" && (
              <>
                <div
                  className="documentItem"
                  onClick={() =>
                    handleDocumentClick("incorporationCertificate")
                  }
                >
                  <div className="documentThumbnail">
                    <img
                      src={
                        kycDetails?.incorporationCertificate ||
                        "/placeholder.svg"
                      }
                      alt="Certificate of Incorporation"
                    />
                  </div>
                  <div className="documentLabel">
                    Certificate of Incorporation
                  </div>
                </div>
                <div
                  className="documentItem"
                  onClick={() => handleDocumentClick("vatCertificate")}
                >
                  <div className="documentThumbnail">
                    <img
                      src={kycDetails?.vatCertificate || "/placeholder.svg"}
                      alt="VAT Registration"
                    />
                  </div>
                  <div className="documentLabel">VAT Registration</div>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="verificationSection">
          <h2>Verification Notes</h2>
          <textarea
            className="notesTextarea"
            value={verificationNotes || kycDetails?.verificationNotes}
            onChange={handleNotesChange}
            placeholder="Add any notes about this verification..."
            disabled={kycDetails?.isGovIdVerified === 1}
          ></textarea>

          {kycDetails?.isGovIdVerified === 0 && (
            <div className="actionButtons">
              <button className="approveButton" onClick={handleApprove}>
                {updatingKyc ? "Approving..." : "Approve Verification"}
              </button>
              <button className="rejectButton" onClick={handleReject}>
                Reject Verification
              </button>
            </div>
          )}

          {kycDetails?.isGovIdVerified === 1 && (
            <div className="statusMessage">
              <div className="approvedMessage">
                This verification has been approved.
              </div>
            </div>
          )}

          {kycDetails?.govIdDeclineCount > 0 && (
            <div className="statusMessage">
              <div className="rejectedMessage">
                This verification has been rejected.
              </div>
            </div>
          )}
        </section>
      </div>

      {activeDocument && (
        <div className="documentViewer">
          <div className="documentViewerContent">
            <div className="documentViewerHeader">
              <h3>
                {activeDocument === "supportiveDocFront" && "ID Front"}
                {activeDocument === "supportiveDocBack" && "ID Back"}
                {activeDocument === "faceImage" && "Selfie with ID"}
                {activeDocument === "incorporationCertificate" &&
                  "Certificate of Incorporation"}
                {activeDocument === "vatCertificate" && "VAT Registration"}
              </h3>
              <button className="closeButton" onClick={closeDocumentViewer}>
                ×
              </button>
            </div>
            <div className="documentViewerImage">
              <img
                src={kycDetails[activeDocument] || "/placeholder.svg"}
                alt={activeDocument}
              />
            </div>
          </div>
        </div>
      )}

      {showRejectionForm && (
        <div className="rejectionModal">
          <div className="rejectionModalContent">
            <h3>Reject Verification</h3>
            <p>Please provide a reason for rejecting this verification:</p>
            <textarea
              className="rejectionTextarea"
              value={rejectionReason}
              onChange={handleRejectionReasonChange}
              placeholder="Reason for rejection..."
            />
            <div className="rejectionModalButtons">
              <button
                className="confirmRejectButton"
                onClick={confirmRejection}
                disabled={rejectingKyc}
              >
                {rejectingKyc ? "Rejecting..." : " Confirm Rejection"}
              </button>
              <button className="cancelButton" onClick={cancelRejection}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KycVerificationReview;
